/**
 * Die Fortschrittsanzeige beim Erzeugen von Probestück und Lied. (11.10.2026, Ticket 3074)
 *
 * Martin, 07.10. 22:49: *„Es gab keine Fortschrittsanzeige, nur am Ende das Ergebnis.“* Bis das Lied da ist, vergehen 30 bis 90
 * Sekunden. Jetzt steht in dieser Zeit eine Karte im Chat: drei Stufen — Text prüfen, Komponieren, Prüfung — und die echte Zeit.
 *
 * ⚠️ Kein Prozentbalken. Der Musikdienst meldet keinen Fortschritt; eine Prozentzahl wäre erfunden. Die Karte zeigt nur, was der
 *    Server wirklich meldet (shared/lied-fortschritt.ts): welche Stufe läuft, welche vorbei ist, und wie lange es schon dauert.
 *    „Erledigt“ steht nur an Stufen, die der Server schon hinter sich hat — die Prüfung ist offen, bis sie läuft.
 * ⚠️ Die Karte ist ein Gast: sie entsteht mit dem ersten Ereignis und verschwindet mit „fertig“, mit dem Ergebnis der Karte ODER
 *    mit dem Ende des Stroms (`schliesseLiedFortschritt`, frontend/app.ts: streamChat → onEnde). Das Letzte ist wichtig (Prüfbefund
 *    Opus 11.10.2026): reißt der Strom ab oder bricht er ab (Stopp mitten im Lied, Netz weg), käme nie ein „fertig“ — die Uhr liefe
 *    weiter („Lyria komponiert … 7:40“), obwohl das Lied längst in der Glocke liegt. Ein Neuladen mittendrin zeigt sie nicht; das
 *    Ergebnis kommt trotzdem.
 * ⚠️ EINE Karte für alle Läufe (sie hängt am Werkzeug, nicht am Aufruf): zwei Lieder gleichzeitig teilen sich die Anzeige.
 * ⚠️ Barrierefrei: die Stufen sind eine Liste, die laufende trägt `aria-current="step"`, der Zustand steht auch als Text. Gesagt
 *    wird (role=status) nur der Satz bei einem Stufenwechsel — der Zähler tickt stumm. Ruhe-Einstellung: keine Bewegung.
 */
import {
  ANBIETER_ANZEIGE,
  FORTSCHRITT_SCHRITTE,
  SCHRITT_NAME,
  fortschrittAus,
  schrittNr,
  type FortschrittSchritt,
  type LiedFortschritt,
} from "../../shared/lied-fortschritt.js";
import { icon } from "./icons.js";

const ZUSTAND_TEXT = { fertig: "erledigt", aktiv: "läuft", offen: "folgt" } as const;
type Zustand = keyof typeof ZUSTAND_TEXT;

/** m:ss — wie auf der Karte des fertigen Lieds. */
export function zeitText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Der Satz unter den Stufen: was gerade geschieht — nur Wahres, nichts über die Restzeit. */
export function standSatz(f: LiedFortschritt): string {
  if (f.schritt === "komponieren") {
    return f.anbieter ? `${ANBIETER_ANZEIGE[f.anbieter]} komponiert …` : "Der Musikdienst komponiert …";
  }
  if (f.schritt === "pruefung") return "Das Stück liegt vor — Länge, Gesang und Sprache werden geprüft …";
  return "Der Text wird geprüft …";
}

function zustandVon(nr: number, aktuell: number): Zustand {
  // Die Stufe „Text prüfen“ ist beim ersten Ereignis schon bestanden (der Server prüft vor dem Start): sie ist erledigt,
  // sobald irgendeine spätere läuft.
  return nr < aktuell ? "fertig" : nr === aktuell ? "aktiv" : "offen";
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, inhalt?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (inhalt !== undefined) e.textContent = inhalt;
  return e;
}

/** Die leere Karte; `zeigeLiedFortschritt` füllt sie mit dem ersten Ereignis. */
export function baueLiedFortschritt(): HTMLElement {
  const karte = el("div", "bubble system tool-progress tool-card tool-music-card tool-lied lied-fortschritt");
  karte.setAttribute("role", "group");
  karte.append(el("p", "lied-kicker"), el("div", "tool-card-title lied-titel"));
  const liste = el("ol", "lied-stufen");
  liste.setAttribute("aria-label", "Fortschritt");
  for (const schritt of FORTSCHRITT_SCHRITTE) {
    const li = el("li", "lied-stufe");
    li.dataset.schritt = schritt;
    const punkt = el("span", "lied-stufe-punkt");
    punkt.setAttribute("aria-hidden", "true");
    punkt.appendChild(icon("check", { size: 12 }));
    li.append(punkt, el("span", "lied-stufe-name"), el("span", "lied-stufe-zustand"));
    liste.appendChild(li);
  }
  const stand = el("p", "lied-fortschritt-stand");
  stand.setAttribute("role", "status");
  const zeit = el("span", "lied-fortschritt-zeit");
  zeit.setAttribute("aria-hidden", "true");
  stand.append(el("span", "lied-fortschritt-satz"), zeit);
  karte.append(liste, stand);
  return karte;
}

/** Wann der Lauf begann (ms) und der Zähler dazu — je Karte. */
const start = new WeakMap<HTMLElement, number>();
const zaehler = new WeakMap<HTMLElement, number>();

function tick(karte: HTMLElement): void {
  const begonnen = start.get(karte);
  const z = karte.querySelector(".lied-fortschritt-zeit");
  if (begonnen !== undefined && z) z.textContent = zeitText(Date.now() - begonnen);
}

/** Den Zähler stoppen — die Karte geht weg oder ist nicht mehr im Seitenbaum. */
export function stoppeLiedFortschritt(karte: HTMLElement): void {
  const id = zaehler.get(karte);
  if (id !== undefined) window.clearInterval(id);
  zaehler.delete(karte);
}

/** Die Karte, die gerade im Chat steht (höchstens eine). */
let aktuelle: HTMLElement | null = null;

/**
 * Ein `tool_progress`-Ereignis von generate_music: die Karte bauen (beim ersten Mal, `einsetzen` setzt sie in den Chat) und fortschreiben.
 * `false`, wenn das Ereignis keinen lesbaren Fortschritt trägt (älterer Server) — dann zeigt der Aufrufer seine einfache Zeile.
 */
export function liedFortschrittEreignis(p: { fortschritt?: unknown; elapsedMs?: number }, einsetzen: (karte: HTMLElement) => void): boolean {
  const f = fortschrittAus(p.fortschritt);
  if (!f) return false;
  if (!aktuelle?.isConnected) {
    if (aktuelle) stoppeLiedFortschritt(aktuelle);
    aktuelle = baueLiedFortschritt();
    einsetzen(aktuelle);
  }
  zeigeLiedFortschritt(aktuelle, f, p.elapsedMs);
  return true;
}

/** Die Karte wegnehmen und die Uhr anhalten — „fertig“, Ergebnis, Ende oder Abbruch des Stroms. Ohne Karte: nichts. */
export function schliesseLiedFortschritt(): void {
  const karte = aktuelle;
  aktuelle = null;
  if (!karte) return;
  stoppeLiedFortschritt(karte);
  karte.remove();
}

let warImAnruf = false;

/**
 * Der Anruf-Zustand (frontend/app.ts, `syncVoiceModeExclusivity`: läuft bei JEDEM Wechsel des Anrufs). Endet der Anruf, solange eine Karte
 * dasteht, kommt nie ein „fertig“ — der HTTP-Chat meldet sein Ende über `onEnde`, der Anruf hat keinen solchen Strom. Die Uhr liefe
 * weiter („Lyria komponiert … 7:40“). (Integrationsprüfung 11.10.2026) Das Ergebnis kommt trotzdem: als Karte im Chat und in der Glocke.
 * Nur der ÜBERGANG „im Anruf“ → „nicht mehr“ schließt; ein Chat-Lauf ohne Anruf bleibt unberührt.
 */
export function anrufZustandFuerLied(verbunden: boolean): void {
  const vorbei = warImAnruf && !verbunden;
  warImAnruf = verbunden;
  if (vorbei) schliesseLiedFortschritt();
}

/**
 * Ein Ereignis des Servers anzeigen. `elapsedMs` ist die echte Zeit seit dem Start des Laufs; die Sekunden zählt die Karte
 * selbst weiter und gleicht sich mit jedem Ereignis neu an.
 */
export function zeigeLiedFortschritt(karte: HTMLElement, f: LiedFortschritt, elapsedMs?: number): void {
  const aktuell = schrittNr(f.schritt);
  karte.dataset.stufe = f.stufe;
  karte.setAttribute("aria-label", f.stufe === "probe" ? "Probestück entsteht" : "Lied entsteht");
  const kicker = karte.querySelector(".lied-kicker");
  if (kicker) kicker.textContent = f.stufe === "probe" ? "Probestück entsteht · 30 Sekunden · zählt nicht" : "Lied entsteht";
  const titel = karte.querySelector(".lied-titel");
  if (titel) {
    titel.textContent = (f.titel ?? "").replace(/^Probestück:\s*/, "");
    (titel as HTMLElement).hidden = !titel.textContent;
  }
  FORTSCHRITT_SCHRITTE.forEach((schritt: FortschrittSchritt, nr) => {
    const li = karte.querySelector<HTMLElement>(`.lied-stufe[data-schritt="${schritt}"]`);
    if (!li) return;
    const zustand = zustandVon(nr, aktuell);
    li.dataset.zustand = zustand;
    if (zustand === "aktiv") li.setAttribute("aria-current", "step");
    else li.removeAttribute("aria-current");
    const name = li.querySelector(".lied-stufe-name");
    if (name) name.textContent = zustand === "fertig" ? SCHRITT_NAME[schritt].fertig : SCHRITT_NAME[schritt].grund;
    const z = li.querySelector(".lied-stufe-zustand");
    if (z) z.textContent = `, ${ZUSTAND_TEXT[zustand]}`;
  });
  const satz = karte.querySelector(".lied-fortschritt-satz");
  const neu = standSatz(f);
  // Nur bei einem echten Wechsel neu setzen: gleicher Text, keine zweite Ansage.
  if (satz && satz.textContent !== neu) satz.textContent = neu;
  if (typeof elapsedMs === "number" && Number.isFinite(elapsedMs) && elapsedMs >= 0) start.set(karte, Date.now() - elapsedMs);
  else if (!start.has(karte)) start.set(karte, Date.now());
  tick(karte);
  if (!zaehler.has(karte)) {
    const id = window.setInterval(() => {
      // Aus dem Seitenbaum genommen (Ergebnis da, Verlauf neu gebaut): der Zähler hört auf.
      if (!karte.isConnected) return stoppeLiedFortschritt(karte);
      tick(karte);
    }, 1000);
    zaehler.set(karte, id);
  }
}
